import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  FormBuilder,
  Validators,
  FormGroup,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { default as swal } from 'sweetalert2';
import { MessageService } from '@app/shared/services';
import { AuctionService, InitAdminService } from '@app/admin/services';
import {
  ExperimentalTarget,
  ExperimentalTargetLabel,
  TypeResponse,
} from '@app/shared/enum';
import { Auction } from '@app/admin/model';
import { first } from 'rxjs/operators';

@Component({
  selector: 'course-auction-detail',
  templateUrl: 'course-auction-detail.component.html',
})
export class CourseAuctionDetailComponent implements OnInit {
  @Input('courseId') courseId: number;
  @Input('auctionId') auctionId: number;

  public auctionForm: FormGroup;
  public submitted = false;
  public validAuctionForm: boolean = true;
  public errorMessage = '';
  public listErrorMessage: any[];

  public currentStep: number = 1;
  public totalSteps: number = 3;

  public experimentalTargets: { value: number; text: string }[];
  public minAuctionStartDate: string;

  public editMode: boolean = false;

  constructor(
    private initAdmin: InitAdminService,
    private loading: MessageService,
    private auctionService: AuctionService,
    private modalService: NgbModal,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.loading.showLoading();
    this.initAdmin.setConfigPage();

    this.onInitForm();
    this.loadExperimentalTargets();
    this.setMinDates();

    this.loading.hideLoading();
  }

  // Getter de conveniência para acessar os controles do formulário no HTML
  get f() {
    return this.auctionForm.controls;
  }

  onInitForm() {
    this.auctionForm = this.formBuilder.group(
      {
        // STEP 1: Informações Básicas do Leilão
        title: ['', [Validators.required, Validators.maxLength(100)]],
        description: ['', [Validators.required, Validators.maxLength(500)]],
        target: [ExperimentalTarget, [Validators.required, Validators.min(1)]], // Objetivo

        // Campo courseId (vem do Input, não visível, mas parte do formulário)
        courseId: [this.courseId, [Validators.required]],

        // STEP 2: Duração e Lances
        startDate: ['', [Validators.required]], // Data de início do leilão
        finishDate: ['', [Validators.required]], // Data de encerramento do leilão
        maxNumberBids: [
          null,
          [Validators.required, Validators.min(1), Validators.max(99)],
        ], // Max Lances (ajustei o max para 99 como exemplo)

        // STEP 3: Configurações Adicionais
        checkTeacherAvailability: [true], // Default true
        whatsAppNotification: [false], // Default false
        emailNotification: [false], // Default false
      },
      {
        validators: this.dateRangeValidator, // Adiciona validador cross-field para datas
      }
    );
  }

  // Validador customizado para garantir que finishDate seja depois de startDate
  dateRangeValidator: ValidatorFn = (
    control: AbstractControl
  ): { [key: string]: any } | null => {
    const startDate = control.get('startDate')?.value;
    const finishDate = control.get('finishDate')?.value;

    if (
      startDate &&
      finishDate &&
      new Date(startDate) >= new Date(finishDate)
    ) {
      return { dateRangeInvalid: true };
    }
    return null;
  };

  loadExperimentalTargets() {
    this.experimentalTargets = Array.from(
      ExperimentalTargetLabel.entries()
    ).map(([value, text]) => ({
      value,
      text,
    }));
    // Se o enum começar com 0 que significa "Nenhum", remova-o se não quiser que apareça no select
    // this.experimentalTargets = this.experimentalTargets.filter(t => t.value !== ExperimentalTarget.None);
  }

  setMinDates() {
    const today = new Date();
    // Garante que startDate não pode ser anterior a hoje
    this.minAuctionStartDate = today.toISOString().split('T')[0];

    // Observa mudanças na data de início para ajustar a data mínima de término
    this.f.startDate.valueChanges.subscribe((startDateValue) => {
      if (startDateValue) {
        // Se a data de início for selecionada, a data de término não pode ser antes dela
        // Note: A validação final de finishDate > startDate é feita pelo dateRangeValidator
      }
    });
  }

  // Navegação entre os steps
  goToStep(step: number): void {
    if (step < 1 || step > this.totalSteps) return;

    // Opcional: Validar o step atual antes de avançar
    if (step > this.currentStep) {
      // Tentando avançar
      let isCurrentStepValid = true;
      if (this.currentStep === 1) {
        isCurrentStepValid =
          this.f.title.valid && this.f.description.valid && this.f.target.valid;
      } else if (this.currentStep === 2) {
        isCurrentStepValid =
          this.f.startDate.valid &&
          this.f.finishDate.valid &&
          this.f.maxNumberBids.valid &&
          !this.auctionForm.errors?.dateRangeInvalid;
      }
      if (!isCurrentStepValid) {
        this.auctionForm.markAllAsTouched(); // Marca todos como tocados para exibir erros
        this.errorMessage =
          'Por favor, preencha todos os campos obrigatórios deste passo.';
        this.validAuctionForm = false;
        return;
      }
    }

    this.errorMessage = ''; // Limpa a mensagem de erro ao mudar de passo
    this.validAuctionForm = true;
    this.currentStep = step;
  }

  onSubmit() {
    this.loading.showLoading();
    this.submitted = true;
    this.validAuctionForm = true;
    this.errorMessage = '';
    this.listErrorMessage = [];

    if (this.auctionForm.invalid) {
      this.loading.hideLoading();
      this.submitted = false;
      this.validAuctionForm = false;
      this.errorMessage =
        'Por favor, preencha todos os campos obrigatórios corretamente.';
      this.auctionForm.markAllAsTouched(); // Marca todos os campos como tocados para exibir todos os erros
      return;
    }

    // Obter os dados do formulário
    const auctionData: Auction = this.auctionForm.value;

    // Converter datas para o tipo Date se necessário para o backend
    auctionData.startDate = new Date(this.f.startDate.value);
    auctionData.finishDate = new Date(this.f.finishDate.value);

    this.auctionService
      .save(auctionData.id, auctionData, this.editMode)
      .pipe(first())
      .subscribe(
        (dataReturn: any) => {
          if (dataReturn.typeResponse == TypeResponse.Success) {
            setTimeout(() => {
              this.loading.hideLoading();
              swal({
                type: 'success',
                title: 'Leilão configurado com sucesso!',
                showConfirmButton: false,
                timer: 1500,
              }).then(() => {
                this.modalService.dismissAll('submit');
              });
            }, 1000);
          } else {
            this.loading.hideLoading();
            this.errorMessage = dataReturn.message;
            this.submitted = false;
            if (
              dataReturn.data &&
              dataReturn.data.errors &&
              dataReturn.data.errors.length > 0
            ) {
              this.listErrorMessage = [];
              for (var i = 0; i <= dataReturn.data.errors.length; i++) {
                this.listErrorMessage.push(
                  dataReturn.data.errors[i].Description
                );
              }
            }
          }
          return;
        },
        (error: any) => {
          this.submitted = false;
          this.loading.hideLoading();
          this.errorMessage = !error.ok ? 'Erro ao salvar o leilão.' : error;
          if (error.error) {
            this.listErrorMessage.push(error.error.message);
          }
        }
      );
  }

  onCancel() {
    this.modalService.dismissAll('cancel');
  }
}
